import { useTranslation } from "@outschool/localization";
import {
  favoritesPath,
  learnerProfilesPath,
  pathnameWithoutLocale,
  transactionsPath,
  transcriptsPath,
  websiteHomePath,
} from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { useLocation } from "react-router";

interface UseDashboardLinksParams {
  hasNoLearners: boolean;
  pathname: string;
}

interface DashboardLink {
  isActive: boolean;
  isLocked: boolean;
  label: string;
  to: string;
}

export default function useDashboardLinks({
  hasNoLearners,
  pathname,
}: UseDashboardLinksParams): DashboardLink[] {
  const { t } = useTranslation(
    "client\\components\\parentDashboardV2\\hooks\\useDashboardLinks",
  );
  const learnerProfilesPathValue = learnerProfilesPath();
  const pathnameValue = pathnameWithoutLocale(pathname);
  const links = [
    {
      label: t("Classes"),
      to: websiteHomePath(),
    },
    {
      label: t("Favorites"),
      to: favoritesPath(),
    },
    {
      label: t("Transcripts"),
      to: transcriptsPath(),
    },
    {
      label: t("Transactions"),
      to: transactionsPath(),
    },
    {
      label: t("Learner Profiles"),
      to: learnerProfilesPath(),
    },
  ] as const;

  return links.map(link => {
    const isActive = pathnameValue === link.to;
    const isLearnerProfiles = link.to === learnerProfilesPathValue;
    const isLocked = isLearnerProfiles && hasNoLearners;

    return {
      ...link,
      isActive,
      isLocked,
    };
  });
}

/**
 * @returns true if the current path is a parent dashboard page
 *
 * Piggybacks on the behavior of useDashboardLinks to determine if the current path is a parent dashboard page.
 */
export function useIsParentDashboardPage() {
  const { currentUser } = useSession();
  const location = useLocation();
  const hasNoLearners = (currentUser?.children?.length ?? 0) === 0;
  const links = useDashboardLinks({
    hasNoLearners,
    pathname: location.pathname,
  });
  return links.some(l => l.isActive);
}
